import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useOutletContext } from "react-router"
import { Banner } from "@/components/ui/banner"
import { CardBody, CardBodyHeader, CardBodyHeaderTitle } from "@/components/ui/card"
import { usePatientLayoutContext } from "@/contexts/patient-layout-context"
import { hasDeBetterDocMsnResult } from "@/modules/patients/detail/better-doc/de/lib/has-de-better-doc-msn-result"
import { AttachmentsSection } from "@/modules/patients/detail/progress-note/attachments"
import { GeneralHealthInformationSummary } from "@/modules/patients/detail/progress-note/components/general-health-information-summary"
import { PreventionProgramInlineBanner } from "@/modules/patients/detail/progress-note/components/prevention-program-inline-banner"
import { AnamnesisSection } from "@/modules/patients/detail/progress-note/components/progress-note-editable/anamnesis-section"
import { TreatmentPlanEditableSection } from "@/modules/patients/detail/progress-note/components/progress-note-editable/treatment-plan-section"
import { ProgressNoteFollowUp } from "@/modules/patients/detail/progress-note/components/progress-note-follow-up"
import { ProgressNoteReadOnlyCardSection } from "@/modules/patients/detail/progress-note/components/progress-note-readonly-card-section"
import { Questionnaire } from "@/modules/patients/detail/progress-note/components/questionnaire"
import { RequestType } from "@/modules/patients/detail/progress-note/components/request-type"
import { RequestedMedicationsSummary } from "@/modules/patients/detail/progress-note/components/requested-medications-summary"
import { VitalSignsSummary } from "@/modules/patients/detail/progress-note/components/vital-signs-summary"
import { Doc2U } from "@/modules/patients/detail/progress-note/doc2u"
import { DocumentsMenu } from "@/modules/patients/detail/progress-note/documents-menu"
import { DocumentList } from "@/modules/patients/detail/progress-note/documents/document-list"
import { useAppointmentPreventionPrograms } from "@/modules/patients/detail/progress-note/hooks/useAppointmentPreventionPrograms"
import { useProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { PointOfCareSection } from "@/modules/patients/detail/progress-note/point-of-care"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { RecommendedMedicationsEditableSection } from "@/modules/patients/detail/recommended-medications/recommended-medication-editable-section"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { PatientContextAppointmentFragment$data } from "@/modules/patients/services/__generated__/PatientContextAppointmentFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { QuestionnaireCriticalitySummaryFragment$key } from "@/modules/patients/services/__generated__/QuestionnaireCriticalitySummaryFragment.graphql"
import { QuestionnaireCriticalitySummaryFragment } from "@/modules/patients/services/QuestionnaireCriticalitySummaryFragment"

interface ProgressNoteEditableCardBodyProps {
  patientId: string
  appointmentContext: PatientContextAppointmentFragment$data
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  diagnosisCode: string
}

const Documents = ({
  progressNote,
  diagnosisCode,
}: {
  progressNote: EpisodeOfCareProgressNoteData
  diagnosisCode: string
}) => {
  const { t } = useTranslation()
  const progressNoteFormStore = useProgressNoteFormContext((state) => state.progressNoteForms)
  const progressNoteForm = progressNoteFormStore.get(progressNote.id)
  const hasErrors = progressNoteForm?.betterDocMSNReferralLetterForm?.hasErrors
  const hasDeBetterDocReferralLetterError = progressNoteForm?.deBetterDocReferralLetterForm?.hasErrors

  return (
    <>
      <CardBodyHeader data-testid="document-title">
        <CardBodyHeaderTitle>{t("episodeOfCare.progressNote.documents")}</CardBodyHeaderTitle>
        <DocumentsMenu progressNote={progressNote} diagnosisCode={diagnosisCode} />
      </CardBodyHeader>
      <DocumentList progressNote={progressNote} diagnosisCode={diagnosisCode} />
      {hasErrors && (
        <Banner className="px-4" variant="critical" data-validation-error="">
          {t("episodeOfCare.betterDoc.manualSearch.subTitle")}
        </Banner>
      )}
      {hasDeBetterDocReferralLetterError && (
        <Banner
          className="px-4"
          variant="critical"
          data-validation-error=""
          data-testid="de-better-doc-referral-letter-required-banner">
          {hasDeBetterDocMsnResult(progressNote)
            ? t("episodeOfCare.betterDoc.manualSearch.subTitle")
            : t("episodeOfCare.betterDoc.error.referralLetterRequired")}
        </Banner>
      )}
    </>
  )
}

export const ProgressNoteEditableCardBody = ({
  patientId,
  appointmentContext,
  episodeOfCareProgressNoteData,
  diagnosisCode,
}: ProgressNoteEditableCardBodyProps) => {
  const setProgressNoteCreatedDate = useReferralStoreContext((state) => state.setProgressNoteCreatedDate)
  const { t } = useTranslation()
  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()
  const { appointment, createdAt } = episodeOfCareProgressNoteData

  const { countryPatient } = usePatientLayoutContext()
  const { medgateServiceInfo } = countryPatient

  const preventionPrograms = useAppointmentPreventionPrograms(appointmentContext, patientId)

  const criticalitySummary = useFragment<QuestionnaireCriticalitySummaryFragment$key>(
    QuestionnaireCriticalitySummaryFragment,
    appointment.questionnaireCriticalitySummary,
  )

  useEffect(() => {
    setProgressNoteCreatedDate(new Date(createdAt))
  }, [createdAt, setProgressNoteCreatedDate])

  return (
    <CardBody>
      {appointment.requestType !== "NONE" && <RequestType requestType={appointment.requestType} />}
      <ProgressNoteReadOnlyCardSection
        isEditMode={true}
        title={t("episodeOfCare.progressNote.reasonForEncounter")}
        testId="progress-note-reason">
        <div className="grid gap-4">
          <span>{appointment.reason}</span>
          {appointment.healthOverview?.vitalSign && (
            <VitalSignsSummary vitalSignKey={appointment.healthOverview.vitalSign} />
          )}
          {appointment.healthOverview?.generalHealthInformation && (
            <GeneralHealthInformationSummary
              generalHealthInformationKey={appointment.healthOverview.generalHealthInformation}
            />
          )}
          <RequestedMedicationsSummary appointmentRef={appointment} />
          {preventionPrograms.map((program) => (
            <PreventionProgramInlineBanner
              key={program.id}
              preventionProgram={program}
              patientId={patientId}
              episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
            />
          ))}
          {appointment.questionnaireId && (
            <Questionnaire
              episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
              criticalitySummary={criticalitySummary}
              diagnosisCode={diagnosisCode}
            />
          )}
          <AttachmentsSection
            appointmentAttachments={appointment.appointmentAttachments}
            telephoneLine={appointment.telephoneLine}
            appointmentId={appointment.id}
          />
        </div>
      </ProgressNoteReadOnlyCardSection>
      <AnamnesisSection
        patient={patient}
        appointmentContext={appointmentContext}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        questionnaireId={appointment.questionnaireId}
        questionnaireType={criticalitySummary?.questionnaireType}
      />
      {appointment.canUseDoc2U && <Doc2U appointmentAttachments={appointment.appointmentAttachments} />}
      <PointOfCareSection
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        patientId={patientId}
        medgateServiceInfo={medgateServiceInfo}
        diagnosisCode={diagnosisCode}
      />
      <TreatmentPlanEditableSection
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        diagnosisCode={diagnosisCode}
      />
      <RecommendedMedicationsEditableSection
        progressNote={episodeOfCareProgressNoteData}
        patientLanguage={patient.primaryLanguage}
        diagnosisCode={diagnosisCode}
      />
      <Documents progressNote={episodeOfCareProgressNoteData} diagnosisCode={diagnosisCode} />
      <ProgressNoteFollowUp
        progressNote={episodeOfCareProgressNoteData}
        countryCode={patient.country}
        patientId={patientId}
      />
    </CardBody>
  )
}
